import {
  DamagedItemReturnDetailsExceptionSchema,
  ItemDetailsForProcessingSchema,
  ItemNotReceivedReturnDetailsExceptionSchema,
  RESOLUTION_INSTRUCTIONS,
  UndeclaredItemResolutionSchema,
  UndeclaredItemReturnDetailsExceptionSchema,
} from "@redotech/returnbear-model/return-case";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

// Schema for matched line item info from an order (used to build ItemDetailsForProcessing on backend)
export const MatchedLineItemSchema = z.object({
  orderId: z.string(),
  orderName: z.string(),
  lineItemId: z.number(),
  variantId: z.number(),
  sku: z.string(),
  /** Price in presentment currency (what the customer paid) */
  price: z.string(),
  /** Currency code for the price (presentment currency) */
  currency: z.string().optional(),
  grams: z.number(),
});

export type MatchedLineItem = z.infer<typeof MatchedLineItemSchema>;

// All resolution types: RESOLUTION_INSTRUCTIONS + ACKNOWLEDGE for ITEM_NOT_RECEIVED
const ALL_RESOLUTION_TYPES = [
  ...RESOLUTION_INSTRUCTIONS,
  "ACKNOWLEDGE",
] as const;

// Extended resolution schema that includes matchedLineItem for undeclared items
// Backend will use this to fetch customs data and build full ItemDetailsForProcessing
const ExceptionResolutionSchema_ = z.object({
  exception_id: z.string(),
  resolution: createEnum(ALL_RESOLUTION_TYPES),
  matchedLineItem: MatchedLineItemSchema.optional(),
});

export const ExceptionResolutionSchema: z.ZodType<ExceptionResolution> =
  ExceptionResolutionSchema_;

export interface ExceptionResolution
  extends z.infer<typeof ExceptionResolutionSchema_> {}

export const resolveReturnBearException = {
  name: "resolveReturnBearException" as const,
  input: z.object({
    returnId: zExt.objectId(),
    exceptions: z.array(ExceptionResolutionSchema),
  }),
  output: z.object({ success: z.boolean() }),
};

export type ResolveReturnBearException = ExceptionResolution[];

export type UndeclaredItemExceptionResolution = z.infer<
  typeof UndeclaredItemResolutionSchema
>;

export type DamagedItemReturnDetailsException = z.infer<
  typeof DamagedItemReturnDetailsExceptionSchema
>;

export type ItemNotReceivedReturnDetailsException = z.infer<
  typeof ItemNotReceivedReturnDetailsExceptionSchema
>;

export type UndeclaredItemReturnDetailsException = z.infer<
  typeof UndeclaredItemReturnDetailsExceptionSchema
>;

export type ItemDetailsForProcessing = z.infer<
  typeof ItemDetailsForProcessingSchema
>;
